import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { formatDate, daysLeft } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ChevronRight, ChevronLeft, GripVertical } from "lucide-react";
import { toast } from "sonner";

const COLUMNS = [
  { key: "Design & Punching", color: "border-t-purple-400" },
  { key: "Antrean Mesin", color: "border-t-slate-400" },
  { key: "Proses Bordir", color: "border-t-blue-500" },
  { key: "Finishing & Trimming", color: "border-t-cyan-500" },
  { key: "Siap Ambil", color: "border-t-teal-500" },
  { key: "Selesai", color: "border-t-emerald-500" },
];

export default function Kanban() {
  const [orders, setOrders] = useState([]);
  const [machines, setMachines] = useState([]);
  const [machineFilter, setMachineFilter] = useState("all");
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const load = useCallback(() => { api.get("/orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); api.get("/machines").then((r) => setMachines(r.data)).catch(() => {}); }, [load]);

  const move = async (order, status) => {
    if (order.status === status) return;
    setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, status } : o)));
    try { await api.patch(`/orders/${order.id}/status`, { status }); toast.success(`${order.order_id} → ${status}`); }
    catch (e) { toast.error(apiErr(e)); load(); }
  };

  const step = (order, dir) => {
    const idx = COLUMNS.findIndex((c) => c.key === order.status);
    const next = idx + dir;
    if (next < 0 || next >= COLUMNS.length) return;
    move(order, COLUMNS[next].key);
  };

  const visible = orders.filter((o) => machineFilter === "all" || o.machine_id === machineFilter);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">Antrean Produksi</h1>
          <p className="text-sm text-slate-500">Seret kartu atau gunakan panah untuk memperbarui status produksi.</p>
        </div>
        <div className="w-full sm:w-56">
          <Select value={machineFilter} onValueChange={setMachineFilter}>
            <SelectTrigger data-testid="kanban-machine-filter"><SelectValue placeholder="Filter mesin" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Mesin</SelectItem>
              {machines.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {COLUMNS.map((col) => {
          const cards = visible.filter((o) => o.status === col.key);
          return (
            <div
              key={col.key}
              data-testid={`kanban-column-${col.key.toLowerCase().replace(/\s|&|\//g, "-")}`}
              className={cn("kanban-col rounded-xl bg-slate-100/70 border-t-4 p-2 min-h-[200px]", col.color, overCol === col.key && "drag-over")}
              onDragOver={(e) => { e.preventDefault(); setOverCol(col.key); }}
              onDragLeave={() => setOverCol(null)}
              onDrop={() => {
                setOverCol(null);
                const o = orders.find((x) => x.id === dragId);
                if (o) move(o, col.key);
                setDragId(null);
              }}
            >
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="text-xs font-bold text-slate-700">{col.key}</span>
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-white px-1.5 text-xs font-bold text-slate-500">{cards.length}</span>
              </div>
              <div className="space-y-2 mt-1">
                {cards.map((o) => {
                  const dl = daysLeft(o.target_date);
                  return (
                    <div
                      key={o.id}
                      draggable
                      onDragStart={() => setDragId(o.id)}
                      data-testid={`kanban-card-${o.order_id}`}
                      className="kanban-card group rounded-lg border border-slate-200 bg-white p-3 shadow-sm cursor-grab hover:shadow-md"
                    >
                      <div className="flex items-start justify-between">
                        <span className="font-mono text-[11px] text-blue-600">{o.order_id}</span>
                        <GripVertical className="h-3.5 w-3.5 text-slate-300" />
                      </div>
                      <p className="mt-1 text-sm font-semibold text-slate-800 leading-tight">{o.customer_name}</p>
                      <p className="text-xs text-slate-500 truncate">{o.design_name}</p>
                      <div className="mt-1.5 flex items-center justify-between">
                        <span className="text-[10px] font-medium text-slate-400">{o.machine_id || "-"} · {o.qty} pcs</span>
                        {dl !== null && dl <= 2 && o.status !== "Selesai" && (
                          <span className="text-[10px] font-bold text-rose-500">{dl < 0 ? "Telat" : `${dl}h`}</span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center justify-between opacity-60 group-hover:opacity-100 transition-opacity">
                        <button data-testid={`kanban-prev-${o.order_id}`} onClick={() => step(o, -1)} className="rounded p-1 hover:bg-slate-100 text-slate-500"><ChevronLeft className="h-3.5 w-3.5" /></button>
                        <button data-testid={`kanban-next-${o.order_id}`} onClick={() => step(o, 1)} className="rounded p-1 hover:bg-slate-100 text-slate-500"><ChevronRight className="h-3.5 w-3.5" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
